/* eslint-disable no-nested-ternary */
import * as ExcelJS from 'exceljs';

import { formatVlanIdsWithUt } from '../export-helpers';
import { buildExportHeaderRows } from './header-rows';
import { type NodeExportData, type ExportHeaderInfo } from './types';

const TABLE_HEADERS = [
  'Name',
  'Model',
  'IP',
  'Port',
  'Connect Node',
  'Connect Port',
  'VLAN IDs',
  'PoE',
  'DHCP',
  'Notes',
];

export const buildSwitchesExcelBlob = async (
  switchesData: NodeExportData[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
): Promise<{ blob: Blob; filename: string }> => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Switches');

  let dataStartRow = 1;
  if (headerInfo) {
    const headerRows = buildExportHeaderRows(headerInfo, TABLE_HEADERS.length);
    headerRows.forEach((row) => sheet.addRow(row));
    const lastHeaderRow = headerRows.length;
    for (let r = 1; r <= lastHeaderRow; r += 1) {
      const row = sheet.getRow(r);
      row.eachCell((cell) => {
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
          horizontal: 'left',
        };
      });
      row.getCell(1).font = { bold: true };
    }
    sheet.getRow(1).getCell(1).font = { bold: true, size: 12 };
    if (lastHeaderRow > 1) {
      sheet.mergeCells(1, 1, 1, TABLE_HEADERS.length);
    }
    dataStartRow = headerRows.length + 1;
  }

  sheet.addRow(TABLE_HEADERS);

  sheet.getRow(dataStartRow).eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDCE6F1' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  let startRow = dataStartRow + 1;

  switchesData.forEach((switchData) => {
    const ipCount = switchData.IP.length;
    const portCount = switchData.Port.length;
    const maxRows = Math.max(ipCount, portCount);

    for (let i = 0; i < maxRows; i += 1) {
      const portObj = switchData.Port[i] || {};
      const vlanStr = portObj.vlanIds?.length
        ? formatVlanIdsWithUt(portObj.vlanIds, portObj.untaggedVlan)
        : '';
      const poeStr =
        portObj.isPoeCapable === false
          ? '-'
          : portObj.poeEnabled
            ? 'Yes'
            : 'No';

      const rowValues = [
        switchData.Name,
        switchData.Model,
        switchData.IP.map((ip) =>
          ip.ip && ip.ip.trim() !== ''
            ? ip.subnetMask && ip.subnetMask.trim() !== ''
              ? `${ip.ip} (${ip.subnetMask})`
              : ip.ip
            : '',
        )
          .filter((ip) => ip !== '')
          .join('\n'),
        portObj.name || '',
        portObj.connectNode || '',
        portObj.connectPort || '',
        vlanStr,
        poeStr,
        portObj.dhcpEnabled ? 'Yes' : 'No',
        switchData.Notes || '',
      ];

      const row = sheet.addRow(rowValues);
      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }

    const endRow = startRow + maxRows - 1;
    if (endRow > startRow) {
      sheet.mergeCells(`A${startRow}:A${endRow}`);
      sheet.mergeCells(`B${startRow}:B${endRow}`);
      sheet.mergeCells(`C${startRow}:C${endRow}`);
      sheet.mergeCells(`J${startRow}:J${endRow}`);
    }

    startRow += maxRows;
  });

  const MAX_HEADER_COL_WIDTH = 70;
  sheet.columns?.forEach((col, idx) => {
    let maxLength = 0;
    col.eachCell?.({ includeEmpty: true }, (cell: any) => {
      const cellValue = cell?.value ? cell.value.toString() : '';
      maxLength = Math.max(maxLength, cellValue.length);
    });
    const w = maxLength + 2;
    col.width = idx < 2 ? Math.min(w, MAX_HEADER_COL_WIDTH) : w;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return { blob, filename: `${filename}.xlsx` };
};

export const exportSwitchesToExcel = async (
  switchesData: NodeExportData[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
) => {
  const { blob, filename: fname } = await buildSwitchesExcelBlob(
    switchesData,
    filename,
    headerInfo,
  );
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  link.click();
};
